import { createClient } from '@zg-rentals/http-client';
import type { HpApiResponseDto, HpApiWebApiError } from '@zg-rentals/ts-hotpads-api-client';
import { setClient } from '@zg-rentals/ts-hotpads-api-client/client';
import { fetchCsrfToken } from './fetchCsrfToken';

// Partial because the parsed JSON is untrusted at this boundary; the field
// names still type-check against the generated envelope contract.
type V3Envelope = Partial<HpApiResponseDto<unknown>>;

/**
 * A v3 envelope-level failure, carrying the structured `reason` so call sites can
 * branch on it instead of parsing the message.
 */
export class HotpadsApiV3Error extends Error {
  constructor(
    readonly reason: HpApiWebApiError | undefined,
    message: string,
  ) {
    super(message);
    this.name = 'HotpadsApiV3Error';
  }
}

/** Reads the v3 `reason` off a rejected mutation/query error, if it carries one. */
export function v3ErrorReason(error: unknown): HpApiWebApiError | undefined {
  return error instanceof HotpadsApiV3Error ? error.reason : undefined;
}

/**
 * Default client for all generated @zg-rentals/ts-hotpads-api-client calls,
 * registered once at bootstrap (see providers.tsx) so individual call sites
 * cannot forget it.
 * - Injects the csrfToken header on mutating requests; the backend rejects
 *   v3 writes without it and the generated clients have no CSRF handling.
 *   Fails fast when no token is available instead of sending a doomed write.
 * - Rejects with a HotpadsApiV3Error on any JSON envelope carrying an error, so
 *   failures reach React Query error state (and meta.context telemetry) with the
 *   reason intact. This covers both 2xx-with-error and the 4xx statuses the
 *   backend uses for auth failures; throwing here preempts ky's own HTTPError,
 *   whose body would leave `reason` unparsed.
 * - logging: false — errors are already logged once by the query/mutation
 *   cache onError handlers; transport logging would double-report.
 */
export function configureHotpadsApiV3Client(): void {
  const client = createClient({
    credentials: 'include',
    logging: false,
    monitoring: true,
    redirect: 'error',
    timeout: 60 * 1000,
  }).extend({
    hooks: {
      beforeRequest: [
        async (request) => {
          if (request.method === 'GET') {
            return;
          }
          const csrfToken = await fetchCsrfToken();
          if (!csrfToken) {
            throw new Error(`Could not fetch a csrfToken for ${request.method} ${request.url}`);
          }
          request.headers.set('csrfToken', csrfToken);
        },
      ],
      afterResponse: [
        async (_request, _options, response) => {
          if (!response.headers.get('content-type')?.includes('application/json')) {
            return response;
          }
          const body = (await response.clone().json().catch(() => undefined)) as V3Envelope | undefined;
          if (body?.error) {
            throw new HotpadsApiV3Error(
              body.error.reason,
              `hotpads-api v3 error: ${body.error.reason ?? 'UNKNOWN'}: ${body.error.message ?? response.url}`,
            );
          }
          return response;
        },
      ],
    },
  });

  setClient(client);
}
